/**
 * Loose-ball collection assist (§4 "make collecting the ball easier").
 *
 * A CLIENT-SIDE INPUT assist, not a sim change: it nudges the controlled player's
 * desired move direction (cmd.moveX/moveZ) toward a loose ball's intercept point
 * when the ball is reasonably ahead and controllable, so you walk onto it naturally
 * instead of circling. It assigns NO possession and moves NO ball — the sim's own
 * capture + first-touch trap do that, unchanged. Because it only shapes the human's
 * InputCommand (exactly like buildInputCommand does), it:
 *   - is deterministic-irrelevant (the sim stays a pure function of its inputs),
 *   - never touches AI players (they generate their own inputs), so it cannot
 *     perturb any AI-vs-AI acceptance soak, and
 *   - is SHARED: practice, the Test Arena and online all call it on the same seam.
 *
 * Guards (all required before any steering): the ball is loose, the player is
 * actually moving, the ball is within collection range AND inside a ~120° frontal
 * cone of the intended move, the ball is at a controllable speed/height, and no
 * OPPONENT has a clearly better claim (closer to the intercept). The steer is a
 * small, capped blend — assistance, never a guaranteed grab or a magnet from range.
 *
 * AERIAL EXTENSION (playtest: "I can't kick in air" — the sim's strike envelope
 * is fine to 1.6 m standing; the real gap was that this assist only ever steered
 * toward a ground/rolling ball, so the player was never walked UNDER a dropping
 * one in time). When the loose ball is AIRBORNE (above `AERIAL_MIN_HEIGHT` and not
 * `grounded`), skip the ground speed/height "uncontrollable" gate entirely and
 * steer toward its predicted BALLISTIC LANDING point instead of its current/led
 * position — a simple projectile forecast (current pos/vel + gravity, drag and
 * Magnus curl ignored on purpose, same "close enough" approximation philosophy as
 * the AI's own imperfect ball predictor). The existing range/cone/claim gates
 * below apply to that landing point exactly as they do to a ground intercept, so
 * a towering clearance landing 40 m away is still correctly ignored.
 */
import {
  BALL_RADIUS,
  GRAVITY,
  MAX_PLAYERS,
  POSSESSION_MAX_HEIGHT,
  type InputCommand,
  type WorldState,
} from '@fg/shared/sim';

/** Above this planar ball speed the ball is not controllable — no assist (m/s). */
const COLLECT_MAX_BALL_SPEED = 12;
/** Seconds of ball velocity to lead when picking the intercept point. */
const COLLECT_LEAD_S = 0.18;
/** An opponent must be closer than the human by MORE than this to veto the assist
 *  (so a near-tie still assists the human — "useful help, not guaranteed"). */
const OPP_CLAIM_MARGIN = 0.4;
/** Above this height (m) and not resting, the ball counts as AIRBORNE for this
 *  assist — steer toward the predicted landing point instead of the current
 *  position/lead. Below it (or once `grounded`), the ordinary ground path runs. */
const AERIAL_MIN_HEIGHT = 0.5;
/** Cap on how far into the future the ballistic forecast is allowed to look (s) —
 *  a very high, slow-to-land ball (a towering clearance) shouldn't drag the steer
 *  toward a point several seconds away; the range/cone gates do the real work,
 *  this just keeps the forecast itself sane. */
const AERIAL_MAX_LEAD_S = 3;

/**
 * Pure projectile forecast: where a body at (px,py,pz) with velocity (vx,vy,vz)
 * under constant `gravity` (m/s², negative) reaches height `targetY`, ignoring
 * drag and any spin — deliberately approximate, not a real aerodynamic model.
 * Returns the FIRST future (t > 0) crossing; `t` is clamped to 0 if the ball is
 * already at/below `targetY` or the (degenerate) inputs never cross it.
 *
 * Solves 0.5·gravity·t² + vy·t + (py − targetY) = 0 for the smallest positive
 * root. Exported standalone (no WorldState/InputCommand) so it is trivial to
 * unit-test as pure math.
 */
export function predictBallisticLanding(
  px: number,
  py: number,
  pz: number,
  vx: number,
  vy: number,
  vz: number,
  targetY: number,
  gravity: number,
): { x: number; z: number; t: number } {
  const a = 0.5 * gravity;
  const b = vy;
  const c = py - targetY;
  let t = 0;
  if (Math.abs(a) < 1e-9) {
    // Degenerate (no gravity) — fall back to linear motion in y.
    if (Math.abs(b) > 1e-9) {
      const lin = -c / b;
      if (lin > 1e-6) t = lin;
    }
  } else {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const sq = Math.sqrt(disc);
      const t1 = (-b + sq) / (2 * a);
      const t2 = (-b - sq) / (2 * a);
      const lo = Math.min(t1, t2);
      const hi = Math.max(t1, t2);
      // Exactly one root is positive whenever py > targetY under real gravity
      // (their product c/a is negative) — take the first one we cross.
      if (lo > 1e-6) t = lo;
      else if (hi > 1e-6) t = hi;
    }
  }
  return { x: px + vx * t, z: pz + vz * t, t };
}

/** Tunable envelope for the assist — so competitive matches and Solo Free Practice
 *  can use DIFFERENT strengths from the SAME code (the mode passes its config). */
export interface CollectionAssistConfig {
  /** Furthest the assist ever engages (m). */
  maxRange: number;
  /** Inside this, the steer is at full strength (m). */
  strongRange: number;
  /** cos of the half-angle of the frontal collection cone. */
  coneCos: number;
  /** Max blend of the move direction toward the ball inside the strong range. */
  steer: number;
  /** Weaker steer outside the strong range (fraction of `steer`). */
  steerFar: number;
}

/** Match default — modest help, never guaranteed. Used everywhere unless a mode
 *  overrides it. Do NOT strengthen this for training; pass a config instead. */
export const MATCH_COLLECTION: CollectionAssistConfig = {
  maxRange: 1.5, strongRange: 0.75, coneCos: 0.5 /* 120° */, steer: 0.35, steerFar: 0.6,
};

/** Solo Free Practice — stronger, ISOLATED from competitive play (§9): wider ~140°
 *  cone, larger strong range, firmer steer, since there are no defenders to beat. */
export const SOLO_TRAINING_COLLECTION: CollectionAssistConfig = {
  maxRange: 1.5, strongRange: 0.8, coneCos: Math.cos((70 * Math.PI) / 180) /* ~140° */,
  steer: 0.5, steerFar: 0.7,
};

export interface CollectionAssistDebug {
  active: boolean;
  reason: string;
  humanClaim: number; // human distance to the intercept (m), or NaN
  oppClaim: number; // nearest opponent distance to the intercept (m), or Infinity
  interceptX: number;
  interceptZ: number;
  /** True when the intercept above came from the ballistic landing forecast
   *  (airborne ball) rather than the ground lead — arena overlay + tests. */
  aerial: boolean;
}

const IDLE: CollectionAssistDebug = {
  active: false, reason: '—', humanClaim: Number.NaN, oppClaim: Number.POSITIVE_INFINITY,
  interceptX: Number.NaN, interceptZ: Number.NaN, aerial: false,
};

/**
 * Steer `cmd` toward a collectable loose ball, in place. Returns a debug snapshot
 * (also used by the arena overlay). Call AFTER buildInputCommand, once per built
 * input for the controlled human.
 */
export function applyCollectionAssist(
  world: WorldState,
  controlledId: number,
  cmd: InputCommand,
  cfg: CollectionAssistConfig = MATCH_COLLECTION,
): CollectionAssistDebug {
  const b = world.ball;
  const p = world.players;
  if (controlledId < 0) return { ...IDLE, reason: 'no-control' };
  if (b.possessedBy >= 0) return { ...IDLE, reason: 'ball-owned' };

  const moveMag = Math.hypot(cmd.moveX, cmd.moveZ);
  if (moveMag < 0.1) return { ...IDLE, reason: 'not-moving' };

  // AIRBORNE: steer toward the predicted landing point instead of gating on
  // ground speed/height — a dropping ball is exactly the case a human needs
  // help getting UNDER, not a case to declare "uncontrollable" (playtest).
  const aerial = b.grounded !== 1 && b.py > AERIAL_MIN_HEIGHT;

  let ix: number;
  let iz: number;
  if (aerial) {
    const landing = predictBallisticLanding(b.px, b.py, b.pz, b.vx, b.vy, b.vz, BALL_RADIUS, GRAVITY);
    const t = Math.min(landing.t, AERIAL_MAX_LEAD_S);
    ix = b.px + b.vx * t;
    iz = b.pz + b.vz * t;
  } else {
    const ballSpeed = Math.hypot(b.vx, b.vz);
    if (ballSpeed > COLLECT_MAX_BALL_SPEED || b.py > POSSESSION_MAX_HEIGHT) {
      return { ...IDLE, reason: 'ball-uncontrollable' };
    }
    // Intercept point — lead the ball a touch so we head where it is GOING.
    ix = b.px + b.vx * COLLECT_LEAD_S;
    iz = b.pz + b.vz * COLLECT_LEAD_S;
  }
  const dx = ix - p.px[controlledId]!;
  const dz = iz - p.pz[controlledId]!;
  const dist = Math.hypot(dx, dz);
  const dbg: CollectionAssistDebug = { ...IDLE, interceptX: ix, interceptZ: iz, aerial };
  if (dist < 0.05) return { ...dbg, reason: 'on-ball' };
  if (dist > cfg.maxRange) return { ...dbg, reason: 'out-of-range' };

  // Frontal cone: the ball must be roughly the way we are already trying to move —
  // a ball behind you is never magnetically collected.
  const mdx = cmd.moveX / moveMag;
  const mdz = cmd.moveZ / moveMag;
  const tbx = dx / dist;
  const tbz = dz / dist;
  const align = mdx * tbx + mdz * tbz;
  if (align < cfg.coneCos) return { ...dbg, reason: 'not-frontal' };

  // Claim: don't help the human vacuum a ball an opponent is clearly better placed
  // to reach (a near-tie still assists — useful help, not guaranteed possession).
  const myTeam = p.team[controlledId]!;
  const myClaim = dist;
  let oppClaim = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! === myTeam) continue;
    const od = Math.hypot(ix - p.px[i]!, iz - p.pz[i]!);
    if (od < oppClaim) oppClaim = od;
  }
  dbg.humanClaim = myClaim;
  dbg.oppClaim = oppClaim;
  if (oppClaim + OPP_CLAIM_MARGIN < myClaim) return { ...dbg, reason: 'opponent-claim' };

  // Capped steer toward the intercept, stronger up close. Re-normalise so the
  // magnitude (and thus the target speed) is preserved — this only turns the input.
  const strength = cfg.steer * (dist <= cfg.strongRange ? 1 : cfg.steerFar);
  const nmx = mdx + (tbx - mdx) * strength;
  const nmz = mdz + (tbz - mdz) * strength;
  const nlen = Math.hypot(nmx, nmz) || 1;
  cmd.moveX = (nmx / nlen) * moveMag;
  cmd.moveZ = (nmz / nlen) * moveMag;
  // The ball is loose, so the player is not aiming a kick — face the approach.
  cmd.aimYaw = Math.atan2(cmd.moveZ, cmd.moveX);

  dbg.active = true;
  dbg.reason = dist <= cfg.strongRange ? 'strong-assist' : 'steering';
  return dbg;
}
